import { useEffect, useState } from 'react';
import type { TRPC } from '../../../contexts/TRPC';
import type {
  HubspotTicketDetailData,
  MediaItem,
  MediaLoadingState,
} from '../types';
import {
  clearScreenshotLoadingIndex,
  createInitialMediaLoadingState,
  createMediaLoadingState,
} from '../utils';

interface UseHubspotTicketMediaLoadingParams {
  ticketId: string;
  trpc: TRPC | null;
  data: HubspotTicketDetailData | null;
  banner?: MediaItem;
  featureGraphic?: MediaItem;
  screenshots: MediaItem[];
  videos: MediaItem[];
}

export function useHubspotTicketMediaLoading({
  ticketId,
  trpc,
  data,
  banner,
  featureGraphic,
  screenshots,
  videos,
}: UseHubspotTicketMediaLoadingParams) {
  const [mediaLoadingStates, setMediaLoadingStates] =
    useState<MediaLoadingState>(() => createInitialMediaLoadingState());
  const mediaSourceKey = JSON.stringify([
    banner?.uri,
    featureGraphic?.uri,
    screenshots.map((item) => item.uri),
    videos.map((item) => item.uri),
  ]);
  const hasData = !!data;

  useEffect(() => {
    setMediaLoadingStates(createInitialMediaLoadingState());
  }, [ticketId, trpc]);

  useEffect(() => {
    if (!hasData) {
      return;
    }

    setMediaLoadingStates(
      createMediaLoadingState({
        banner,
        featureGraphic,
        screenshots,
        videos,
      }),
    );
  }, [hasData, mediaSourceKey]);

  return {
    mediaLoadingStates,
    markBannerLoaded: () =>
      setMediaLoadingStates((previous) => ({
        ...previous,
        banner: false,
      })),
    markFeatureGraphicLoaded: () =>
      setMediaLoadingStates((previous) => ({
        ...previous,
        featureGraphic: false,
      })),
    markScreenshotLoaded: (index: number) =>
      setMediaLoadingStates((previous) => ({
        ...previous,
        screenshots: clearScreenshotLoadingIndex(previous.screenshots, index),
      })),
    markVideosLoaded: () =>
      setMediaLoadingStates((previous) => ({
        ...previous,
        videos: false,
      })),
  };
}
